import { ArrowUpRight } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import { Link } from '@/components/ui/Link'
import { cn } from '@/lib/utils'
import { ConsoleChip } from './ConsoleChip'
import { MarketingChip } from './MarketingChip'

export const CAI_BRAND_SYSTEM_HREF =
  'https://claude.ai/design/p/bc776e6f-f533-4cdb-ab7b-0a3e9742c80e?via=share'

export interface ExternalRef {
  name: string
  href: string
}

/** Off-page reference: its mark, its name, an out-arrow. */
export function RefLink({ icon, target }: { icon: string; target: ExternalRef }) {
  return (
    <Link
      href={target.href}
      target="_blank"
      rel="noopener noreferrer"
      className="group text-ink hover:text-brand-800 inline-flex items-center gap-1.5"
    >
      {/* None of these marks are square, so height is fixed and width follows. */}
      <img src={icon} alt="" className="h-3.5 w-auto" />
      {target.name}
      <ArrowUpRight className="text-ink-400 group-hover:text-ink-600 size-3.5 transition-colors" />
    </Link>
  )
}

interface CapProps {
  children: React.ReactNode
  onDark?: boolean
  className?: string
}

/** Mono annotation label used above every demo block. */
export function Cap({ children, onDark, className }: CapProps) {
  return (
    <div
      className={cn(
        'font-mono text-[10px] tracking-[0.12em] uppercase',
        onDark ? 'text-ink-300' : 'text-ink-400',
        className
      )}
    >
      {children}
    </div>
  )
}

interface DsSectionProps {
  /** Anchor id, also used by the sidebar nav. */
  id: string
  title: string
  intro?: React.ReactNode
  /** Shows the Marketing Design System chip in the section's upper right. */
  marketing?: boolean
  /** Shows the Console only chip in the section's upper right. */
  consoleOnly?: boolean
  children: React.ReactNode
}

/** One section card of the design-system reference page. */
export function DsSection({ id, title, intro, marketing, consoleOnly, children }: DsSectionProps) {
  return (
    <section id={id} className="border-ink-100 bg-surface scroll-mt-10 rounded-sm border p-8 md:p-12">
      <div className="flex items-start justify-between gap-4">
        <h2 className="text-ink text-3xl font-extrabold tracking-tight">{title}</h2>
        {marketing || consoleOnly ? (
          <div className="flex shrink-0 items-center gap-2">
            {marketing ? <MarketingChip /> : null}
            {consoleOnly ? <ConsoleChip /> : null}
          </div>
        ) : null}
      </div>
      {intro ? <p className="text-md text-ink-500 mt-3 max-w-2xl leading-relaxed">{intro}</p> : null}
      <div className="mt-10 flex flex-col gap-10">{children}</div>
    </section>
  )
}

interface DemoProps {
  /** Mono annotation above the demo area. */
  label: React.ReactNode
  /** Renders the demo area on the dark ink-900 surface. */
  dark?: boolean
  /** Shows the Console only chip at the label's right, for one console-only demo in a shared section. */
  consoleOnly?: boolean
  className?: string
  children: React.ReactNode
}

/** A labeled demo block; `dark` switches to the ink-900 surface. */
export function Demo({ label, dark, consoleOnly, className, children }: DemoProps) {
  return (
    <div>
      {consoleOnly ? (
        <div className="flex items-start justify-between gap-4">
          <Cap>{label}</Cap>
          <ConsoleChip />
        </div>
      ) : (
        <Cap>{label}</Cap>
      )}
      <div className={cn('mt-3', dark && 'bg-ink-900 rounded-sm p-8', className)}>{children}</div>
    </div>
  )
}

interface PhonePreviewProps {
  /** Route to embed, e.g. `/design-system/frames/nav?variant=site`. */
  src: string
  /** Accessible name for the frame. */
  title: string
  height?: number
  /** Grow the frame to its content instead of holding `height`. */
  autoHeight?: boolean
}

/**
 * Phone-width iframe. An iframe carries its own viewport, so `md:` breakpoints inside
 * it resolve at 375px. With `autoHeight` it grows to its content once loaded, so a
 * hardcoded `height` can't go stale and clip the preview.
 */
export function PhonePreview({ src, title, height = 200, autoHeight }: PhonePreviewProps) {
  const frameRef = useRef<HTMLIFrameElement>(null)
  const [measured, setMeasured] = useState<number>()

  useEffect(() => {
    const frame = frameRef.current
    if (!frame || !autoHeight) return

    let resize: ResizeObserver | undefined
    let mutation: MutationObserver | undefined
    const fit = () => {
      // Same-origin, so the frame's own document is readable.
      const doc = frame.contentDocument
      const root = doc?.documentElement
      if (!doc?.body || !root) return
      const measure = () => setMeasured(Math.max(root.scrollHeight, doc.body.scrollHeight))
      measure()
      resize?.disconnect()
      mutation?.disconnect()
      resize = new ResizeObserver(measure)
      resize.observe(doc.body)
      resize.observe(root)
      // index.css pins html/body to height:100%, so their boxes never grow and a
      // ResizeObserver alone would miss content that overflows them.
      mutation = new MutationObserver(measure)
      mutation.observe(doc.body, { subtree: true, childList: true, characterData: true })
    }

    frame.addEventListener('load', fit)
    fit()
    return () => {
      frame.removeEventListener('load', fit)
      resize?.disconnect()
      mutation?.disconnect()
    }
  }, [src, autoHeight])

  return (
    <div className="border-ink-100 w-[375px] max-w-full shrink-0 overflow-hidden rounded-sm border">
      <iframe
        ref={frameRef}
        src={src}
        title={title}
        height={measured ?? height}
        loading={autoHeight ? 'lazy' : undefined}
        className="block w-full"
      />
    </div>
  )
}
